import { useNavigate } from 'react-router';
import { ArrowRight, Radio, Settings, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { RoleSelectionModal } from '../components/RoleSelectionModal';

function CurlingRink() {
  // Sheet: 1000 wide x 112 tall (≈ 9:1 ratio, landscape)
  // House 1 centre: (182, 56)  House 2 centre: (818, 56)
  // Rings: 12ft r=48, 8ft r=32, 4ft r=16, button r=5
  const h1 = { x: 182, y: 56 };
  const h2 = { x: 818, y: 56 };

  return (
    <svg viewBox="0 0 1000 112" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <defs>
        <filter id="rinkRipple" x="-5%" y="-20%" width="110%" height="140%">
          <feTurbulence type="fractalNoise" baseFrequency="0.008 0.06" numOctaves="3" seed="12" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="5" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <radialGradient id="houseGlow1" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60c8e8" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#60c8e8" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="houseGlow2" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#60c8e8" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#60c8e8" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Sheet ice surface */}
      <rect x="0" y="0" width="1000" height="112" fill="rgba(200,230,255,0.04)" rx="2" />

      <g filter="url(#rinkRipple)">
        {/* Sheet boundary */}
        <rect x="1" y="1" width="998" height="110" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" fill="none" rx="1" />

        {/* Centre line — full length */}
        <line x1="0" y1="56" x2="1000" y2="56" stroke="rgba(255,255,255,0.28)" strokeWidth="1" />

        {/* Back lines */}
        <line x1="90" y1="0" x2="90" y2="112" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
        <line x1="910" y1="0" x2="910" y2="112" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />

        {/* Hog lines */}
        <line x1="290" y1="0" x2="290" y2="112" stroke="rgba(255,255,255,0.32)" strokeWidth="1.5" />
        <line x1="710" y1="0" x2="710" y2="112" stroke="rgba(255,255,255,0.32)" strokeWidth="1.5" />

        {/* Tee lines */}
        <line x1={h1.x} y1="0" x2={h1.x} y2="112" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />
        <line x1={h2.x} y1="0" x2={h2.x} y2="112" stroke="rgba(255,255,255,0.22)" strokeWidth="1" />

        {/* House 1 — 12ft blue */}
        <circle cx={h1.x} cy={h1.y} r="48" stroke="#3b82f6" strokeWidth="5" fill="rgba(59,130,246,0.08)" opacity="0.75" />
        {/* House 1 — 8ft white */}
        <circle cx={h1.x} cy={h1.y} r="32" stroke="#e2e8f0" strokeWidth="5" fill="rgba(226,232,240,0.06)" opacity="0.4" />
        {/* House 1 — 4ft red */}
        <circle cx={h1.x} cy={h1.y} r="16" stroke="#ef4444" strokeWidth="5" fill="rgba(239,68,68,0.1)" opacity="0.75" />
        {/* House 1 — button */}
        <circle cx={h1.x} cy={h1.y} r="5" fill="#f1f5f9" opacity="0.7" />

        {/* House 2 — 12ft blue */}
        <circle cx={h2.x} cy={h2.y} r="48" stroke="#3b82f6" strokeWidth="5" fill="rgba(59,130,246,0.08)" opacity="0.75" />
        {/* House 2 — 8ft white */}
        <circle cx={h2.x} cy={h2.y} r="32" stroke="#e2e8f0" strokeWidth="5" fill="rgba(226,232,240,0.06)" opacity="0.4" />
        {/* House 2 — 4ft red */}
        <circle cx={h2.x} cy={h2.y} r="16" stroke="#ef4444" strokeWidth="5" fill="rgba(239,68,68,0.1)" opacity="0.75" />
        {/* House 2 — button */}
        <circle cx={h2.x} cy={h2.y} r="5" fill="#f1f5f9" opacity="0.7" />

        {/* Hack marks */}
        <rect x="34" y="50" width="10" height="12" rx="2" fill="rgba(255,255,255,0.3)" />
        <rect x="956" y="50" width="10" height="12" rx="2" fill="rgba(255,255,255,0.3)" />
      </g>

      {/* Ambient glow under each house */}
      <ellipse cx={h1.x} cy={h1.y} rx="55" ry="30" fill="url(#houseGlow1)" />
      <ellipse cx={h2.x} cy={h2.y} rx="55" ry="30" fill="url(#houseGlow2)" />

      {/* Water shimmer lines across the sheet */}
      {[28, 42, 56, 70, 84].map((y, i) => (
        <line key={i} x1="0" y1={y} x2="1000" y2={y}
          stroke="white" strokeWidth="0.5" opacity={0.03 + (i % 2) * 0.02} />
      ))}
    </svg>
  );
}

export function Options() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // Mixed-role users (sponsor on one account, owner/contributor on another) keep
  // today's Curling Codex landing — deliberately deferred, see feature plan notes.
  const isSponsorOnly =
    !user?.systemAdmin && (user?.accounts.length ?? 0) > 0
    && user!.accounts.every(a => a.role === 'sponsor');

  async function handleSignOut() {
    // Ends the server session too; the guard sends us back to sign-in.
    await signOut();
    navigate('/', { replace: true });
  }

  return (
    <div
      className="relative min-h-screen overflow-hidden"
      style={{ fontFamily: "'Barlow', sans-serif" }}
    >
      {/* ── BACKGROUND PHOTO ── */}
      <div className="absolute inset-0 bg-slate-950">
        <img
          src="https://images.unsplash.com/photo-1687428896920-fae2683df1f3?w=1800&h=1200&fit=crop&auto=format"
          alt="Lake surrounded by rocky mountains under cloudy sky"
          className="w-full h-full object-cover"
          style={{ objectPosition: '50% 35%', opacity: 0.85 }}
        />
      </div>

      {/* ── GRADIENT LAYERS ── */}
      {/* Sky/mountain zone — subtle top vignette */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to bottom, rgba(2,6,12,0.78) 0%, rgba(2,6,12,0.35) 28%, transparent 48%)' }}
      />
      {/* Horizon band — thin darkening to separate zones */}
      <div
        className="absolute inset-x-0"
        style={{
          top: '44%', height: '12%',
          background: 'linear-gradient(to bottom, transparent, rgba(2,6,12,0.18), transparent)',
        }}
      />
      {/* Lake zone — bottom vignette grounds the scene */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(to top, rgba(2,6,12,0.88) 0%, rgba(2,6,12,0.3) 30%, transparent 50%)' }}
      />

      {/* ── UPPER ZONE — mountains: brand + CTA ── */}
      <div
        className="absolute inset-x-0 top-0 flex flex-col items-center justify-center"
        style={{ height: '52%', paddingTop: '20vh' }}
      >
        {/* Main brand */}
        <h1
          className="uppercase leading-none text-center"
          style={{
            fontFamily: "'Cinzel', serif",
            fontWeight: 600,
            fontSize: 'clamp(1.6rem, 5vw, 3.2rem)',
            color: '#ffffff',
            textShadow: '0 2px 48px rgba(0,0,0,0.9), 0 0 80px rgba(0,0,0,0.6)',
            letterSpacing: '0.06em',
            width: '100vw',
          }}
        >
          Black Rock
        </h1>
        <p
          className="uppercase mt-1 text-center"
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(1.4rem, 3.5vw, 2.4rem)',
            letterSpacing: '0.24em',
            background: 'linear-gradient(90deg, #93c5fd 0%, #60c8e8 50%, #93c5fd 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 16px rgba(77,217,247,0.5))',
          }}
        >
          Curling
        </p>

        <div className="mt-7 mb-7" style={{ paddingTop: '10vh' }} />

        {/* App button — system admins manage accounts instead of tracking games */}
        {!user ? (
          <button
            onClick={() => navigate('/live-games')}
            className="group flex items-center gap-3 transition-all duration-200 active:scale-[0.97]"
            style={{
              background: 'rgba(4,12,24,0.7)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(248,113,113,0.28)',
              borderRadius: '12px',
              padding: '18px 34px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = 'rgba(248,113,113,0.6)';
              el.style.boxShadow = '0 8px 40px rgba(0,0,0,0.55), 0 0 28px rgba(248,113,113,0.14)';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = 'rgba(248,113,113,0.28)';
              el.style.boxShadow = '0 8px 32px rgba(0,0,0,0.45)';
            }}
          >
            <Radio className="w-5 h-5 flex-shrink-0 text-red-400" />

            <div>
              <p
                className="text-white font-bold text-left"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', letterSpacing: '0.05em' }}
              >
                View Live Games
              </p>
              <p className="text-left text-xs" style={{ color: 'rgba(252,165,165,0.6)' }}>
                Watch games being recorded right now — no account needed
              </p>
            </div>

            <ArrowRight
              className="w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1"
              style={{ color: 'rgba(248,113,113,0.55)' }}
            />
          </button>
        ) : user?.systemAdmin ? (
          <button
            onClick={() => navigate('/admin')}
            className="group flex items-center gap-3 transition-all duration-200 active:scale-[0.97]"
            style={{
              background: 'rgba(4,12,24,0.7)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(245,158,11,0.28)',
              borderRadius: '12px',
              padding: '18px 34px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = 'rgba(245,158,11,0.6)';
              el.style.boxShadow = '0 8px 40px rgba(0,0,0,0.55), 0 0 28px rgba(245,158,11,0.14)';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = 'rgba(245,158,11,0.28)';
              el.style.boxShadow = '0 8px 32px rgba(0,0,0,0.45)';
            }}
          >
            <ShieldAlert className="w-5 h-5 flex-shrink-0 text-amber-300" />

            <div>
              <p
                className="text-white font-bold text-left"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', letterSpacing: '0.05em' }}
              >
                Account Administration
              </p>
              <p className="text-left text-xs" style={{ color: 'rgba(252,211,77,0.6)' }}>
                Manage every registered user and program
              </p>
            </div>

            <ArrowRight
              className="w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1"
              style={{ color: 'rgba(245,158,11,0.55)' }}
            />
          </button>
        ) : isSponsorOnly ? (
          <button
            onClick={() => navigate('/sponsor')}
            className="group flex items-center gap-3 transition-all duration-200 active:scale-[0.97]"
            style={{
              background: 'rgba(4,12,24,0.7)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(16,185,129,0.28)',
              borderRadius: '12px',
              padding: '18px 34px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = 'rgba(16,185,129,0.6)';
              el.style.boxShadow = '0 8px 40px rgba(0,0,0,0.55), 0 0 28px rgba(16,185,129,0.14)';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.borderColor = 'rgba(16,185,129,0.28)';
              el.style.boxShadow = '0 8px 32px rgba(0,0,0,0.45)';
            }}
          >
            <ShieldCheck className="w-5 h-5 flex-shrink-0 text-emerald-300" />

            <div>
              <p
                className="text-white font-bold text-left"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', letterSpacing: '0.05em' }}
              >
                Sponsor View
              </p>
              <p className="text-left text-xs" style={{ color: 'rgba(110,231,183,0.6)' }}>
                Condensed team results and trends
              </p>
            </div>

            <ArrowRight
              className="w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1"
              style={{ color: 'rgba(16,185,129,0.55)' }}
            />
          </button>
        ) : user?.needsRoleSelection ? (
          <RoleSelectionModal />
        ) : (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={() => navigate('/program')}
              className="group flex items-center gap-3 transition-all duration-200 active:scale-[0.97]"
              style={{
                background: 'rgba(4,12,24,0.7)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(147,197,253,0.22)',
                borderRadius: '12px',
                padding: '18px 34px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                width: '380px',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = 'rgba(147,197,253,0.55)';
                el.style.boxShadow = '0 8px 40px rgba(0,0,0,0.55), 0 0 28px rgba(147,197,253,0.14)';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = 'rgba(147,197,253,0.22)';
                el.style.boxShadow = '0 8px 32px rgba(0,0,0,0.45)';
              }}
            >
              <Settings className="w-5 h-5 flex-shrink-0" style={{ color: 'rgba(147,197,253,0.75)' }} />

              <div>
                <p
                  className="text-white font-bold text-left"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', letterSpacing: '0.05em' }}
                >
                  Program Management
                </p>
                <p className="text-left text-xs" style={{ color: 'rgba(147,197,253,0.6)' }}>
                  Add users, manage teams, etc
                </p>
              </div>

              <ArrowRight
                className="w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1"
                style={{ color: 'rgba(147,197,253,0.55)' }}
              />
            </button>

            <button
              onClick={() => navigate('/CurlingCodex')}
              className="group flex items-center gap-3 transition-all duration-200 active:scale-[0.97]"
              style={{
                background: 'rgba(4,12,24,0.7)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(96,200,232,0.28)',
                borderRadius: '12px',
                padding: '18px 34px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                width: '380px',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = 'rgba(96,200,232,0.6)';
                el.style.boxShadow = '0 8px 40px rgba(0,0,0,0.55), 0 0 28px rgba(77,217,247,0.14)';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = 'rgba(96,200,232,0.28)';
                el.style.boxShadow = '0 8px 32px rgba(0,0,0,0.45)';
              }}
            >
              {/* Mini rings icon */}
              <svg viewBox="0 0 26 26" className="w-5 h-5 flex-shrink-0" fill="none">
                <circle cx="13" cy="13" r="11" stroke="#3b82f6" strokeWidth="2.2" fill="none" opacity="0.8" />
                <circle cx="13" cy="13" r="7" stroke="#ef4444" strokeWidth="2.2" fill="none" opacity="0.8" />
                <circle cx="13" cy="13" r="2.8" fill="#e2e8f0" opacity="0.9" />
                <line x1="2" y1="13" x2="24" y2="13" stroke="white" strokeWidth="0.8" opacity="0.35" />
                <line x1="13" y1="2" x2="13" y2="24" stroke="white" strokeWidth="0.8" opacity="0.35" />
              </svg>

              <div>
                <p
                  className="text-white font-bold text-left"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', letterSpacing: '0.05em' }}
                >
                  Curling Codex
                </p>
                <p className="text-left text-xs" style={{ color: 'rgba(147,197,253,0.6)' }}>
                  Real-time game analytics and tracking
                </p>
              </div>

              <ArrowRight
                className="w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1"
                style={{ color: 'rgba(96,200,232,0.55)' }}
              />
            </button>

            <button
              onClick={() => navigate('/live-games')}
              className="group flex items-center gap-3 transition-all duration-200 active:scale-[0.97]"
              style={{
                background: 'rgba(4,12,24,0.7)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(248,113,113,0.22)',
                borderRadius: '12px',
                padding: '18px 34px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
                width: '380px',
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = 'rgba(248,113,113,0.55)';
                el.style.boxShadow = '0 8px 40px rgba(0,0,0,0.55), 0 0 28px rgba(248,113,113,0.14)';
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = 'rgba(248,113,113,0.22)';
                el.style.boxShadow = '0 8px 32px rgba(0,0,0,0.45)';
              }}
            >
              <Radio className="w-5 h-5 flex-shrink-0 text-red-400" />

              <div>
                <p
                  className="text-white font-bold text-left"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.05rem', letterSpacing: '0.05em' }}
                >
                  View Live Games
                </p>
                <p className="text-left text-xs" style={{ color: 'rgba(252,165,165,0.6)' }}>
                  Watch every game being recorded right now
                </p>
              </div>

              <ArrowRight
                className="w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 group-hover:translate-x-1"
                style={{ color: 'rgba(248,113,113,0.55)' }}
              />
            </button>
          </div>
        )}

      </div>

      {/* Signed-in identity and sign out — fixed top right. Guests browsing
          without an account get neither: there is no session to show or end. */}
      {user && (
        <div className="absolute top-5 right-6 flex items-center gap-4 z-20">
          <span
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontSize: '0.9rem',
              color: 'rgba(147,197,253,0.7)',
              letterSpacing: '0.05em',
            }}
          >
            {user.displayName}
          </span>
          <button
            onClick={() => navigate('/profile')}
            className="uppercase tracking-[0.15em] transition-colors duration-150"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1rem', color: 'rgba(255,255,255,0.65)' }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,1)')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)')}
          >
            Profile
          </button>
          <button
            onClick={handleSignOut}
            className="uppercase tracking-[0.15em] transition-colors duration-150"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1rem', color: 'rgba(255,255,255,0.65)' }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,1)')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)')}
          >
            Sign Out
          </button>
        </div>
      )}

    </div>
  );
}
